/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { useCallback } from 'react';
import { useLocalStorage } from '../../../react-components/useLocalStorage';

// The Agent is one right-hand column (PA-4471); conversation history opens in
// place of the conversation and can never outlive the column.
export type AgentSurfaceState = 'closed' | 'side_panel';

const AGENT_SURFACE_STORAGE_KEY = 'agent-surface';
const AGENT_HISTORY_OPEN_STORAGE_KEY = 'agent-history-open';

const isBooleanValue = (value: boolean) => typeof value === 'boolean';

// The open column survives a reload (deploy reloads, F5) but a fresh visit
// starts closed, so canonical redirects and new tabs don't resurrect it.
const isValidStoredSurface = (value: AgentSurfaceState) => {
  if (value === 'closed') return true;
  if (value !== 'side_panel') return false;
  const navigation = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  return navigation === undefined || navigation.type === 'reload';
};

/**
 * Owns whether the Agent column is open plus the in-place history view, keeping
 * them in sync: closing the column always closes history. `alwaysOpen` is the
 * phone layout, where the Agent chat is the whole app.
 */
export const useAgentSurface = (alwaysOpen = false) => {
  const [agentSurface, setAgentSurfaceRaw] = useLocalStorage<AgentSurfaceState>(
    AGENT_SURFACE_STORAGE_KEY,
    'closed',
    isValidStoredSurface,
  );
  const [isHistoryOpenRaw, setHistoryOpen] = useLocalStorage<boolean>(
    AGENT_HISTORY_OPEN_STORAGE_KEY,
    false,
    isBooleanValue,
  );
  const isAgentPanelOpen = alwaysOpen || agentSurface === 'side_panel';
  const isHistoryOpen = isAgentPanelOpen && isHistoryOpenRaw;

  const setAgentSurface = useCallback(
    (next: AgentSurfaceState) => {
      setAgentSurfaceRaw(next);
      if (next === 'closed') setHistoryOpen(false);
    },
    [setAgentSurfaceRaw, setHistoryOpen],
  );

  const closeAgentPanel = useCallback(() => setAgentSurface('closed'), [setAgentSurface]);
  const showAgentSidePanel = useCallback(() => setAgentSurface('side_panel'), [setAgentSurface]);
  const toggleAgentPanel = useCallback(
    () => setAgentSurface(isAgentPanelOpen ? 'closed' : 'side_panel'),
    [isAgentPanelOpen, setAgentSurface],
  );

  return {
    isAgentPanelOpen,
    isHistoryOpen,
    setHistoryOpen,
    closeAgentPanel,
    showAgentSidePanel,
    toggleAgentPanel,
  };
};
